import * as THREE from 'three'

import BaseDimension from './baseDimension'
import MathsUtils from '@/utils/mathsUtils'
import GlassDesignerTypes from '../types'


// Linear Dimension
// Supports multiple points between the start & end of the dimension
export default class LinearDimension extends BaseDimension {
  constructor(editor, options = {}) {
    super(editor, options)

    this.dimType = options.dimType || GlassDesignerTypes.DimensionTypes.linearGlassSegmentDimension

    this.displayPoint = null      // Display point. If null, uses 1st point
    this.lineDirection = new THREE.Vector3(0, 0, 0)
    this.extendDirection = new THREE.Vector3(0, 0, 0)

    this.startPoint = new THREE.Vector3(0, 0, 0)
    this.midPoint = new THREE.Vector3(0, 0, 0)
    this.endPoint = new THREE.Vector3(0, 0, 0)

    this.seperation = 10      // How far to start the end lengths from the actual position
    this.endLength = 40       // Length of the end cap lines

    this.mainLineScalar = 5.0
    this.mainLineScalarMax = 50.0

    // Ratio to scale visibility of dimension size by zoom level
    this.autoHideEnabled = this.dimType == GlassDesignerTypes.DimensionTypes.linearGlassSegmentDimension || this.dimType == GlassDesignerTypes.DimensionTypes.glassHoleOffsetDimension
    this.minSizeVisibleFactor = 25.0
    this.minSizeVisibleMax = 75.0
    this.autoHideZoomMax = 7.75

    this.autoScaleSmallDimEnabled = this.dimType == GlassDesignerTypes.DimensionTypes.linearGlassSegmentDimension

    // Objects stored for reusing dimensions
    this.startArrowObj = null
    this.endArrowObj = null
    this.startCapLineObj = null
    this.endCapLineObj = null
    this.dimensionLineAObj = null
    this.dimensionLineBObj = null
    this.dimensionLabelObj = null
    
    this.listenMouseEvents = !!options.editable

    // Performance: Cache the update settings so we only update dimension details if the dimension/view scale changes
    // Not every frame in onBeforeRender()
    this._lastRenderDimScale = 0
    this._lastRenderDimLength = 0
    
    this._totalLength = 0
  }

  // Reset the settings & state
  reset() {
    super.reset()

    this.displayPoint = null

    this.seperation = 10      // How far to start the end lengths from the actual position
    this.endLength = 40       // Length of the end cap lines

    this.mainLineScalar = 40.0
    this.mainLineScalarMax = 50.0

    this.autoHideEnabled = this.dimType == GlassDesignerTypes.DimensionTypes.linearGlassSegmentDimension || this.dimType == GlassDesignerTypes.DimensionTypes.glassHoleOffsetDimension
    this.minSizeVisibleFactor = 25.0
    this.minSizeVisibleMax = 75.0
    this.autoHideZoomMax = 7.75

    this.autoScaleSmallDimEnabled = this.dimType == GlassDesignerTypes.DimensionTypes.linearGlassSegmentDimension

    this._lastRenderDimScale = 0
    this._lastRenderDimLength = 0
  }

  // Set the highlighted flag
  setHighlighted(highlighted) {
    super.setHighlighted(highlighted)
    
    if(this.dimensionLabelObj)
      this.dimensionLabelObj.fontSize = this.isHighlighted ? this.highlightFontSize : this.fontSize
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    var dimScale = 1.0 / this.editor.camera.zoom
    let extLineColour = this.editor.getTheme().dimExtLineColour

    // Hide the dimension if it's too small unless we're zooming in
    // Prevents tiny dimensions cluttering the view when viewing the whole shape
    // Note: Don't set visibility on this BaseObject, only the THREE scene object otherwise onBeforeRender() will not be called again
    if(this.sceneObject && this.autoHideEnabled) {
      let minLength = Math.min(dimScale * this.minSizeVisibleFactor, this.minSizeVisibleMax)
      this.sceneObject.visible = this._totalLength > minLength

      // If zoomed out too far, hide dimensions for performance
      if(this.sceneObject.visible) {
        if(dimScale > this.autoHideZoomMax)
          this.sceneObject.visible = false
      }
    }

    // Don't bother with the rest if hidden
    if(!this.sceneObject.visible)
      return

    let dimensionDistance = this._totalLength

    // Only update the details below if the dimension length / view scale changed
    // Helps reduce updates every frame
    if(Math.abs(this._lastRenderDimScale - dimScale) < 0.001 && Math.abs(this._lastRenderDimLength - dimensionDistance) < 0.001)
      return

    this._lastRenderDimScale = dimScale
    this._lastRenderDimLength = dimensionDistance

    // Scale the lines based on zoom
    // Maximum distance based on how big the line is
    // For really small lines (<= 100mm) use 20% the max distance
    var scalarMax = this.mainLineScalarMax
    if(this._totalLength <= 100.0 && this.autoScaleSmallDimEnabled)
      scalarMax *= 0.20
      
    var mainLineDist = Math.min(dimScale * this.mainLineScalar, scalarMax)
    
    // Overall / projected dimensions always a fixed distance away
    if(this.dimType == GlassDesignerTypes.DimensionTypes.linearGlassOverallDimension)
      mainLineDist = this.mainLineScalarMax * 5.5
    if(this.dimType == GlassDesignerTypes.DimensionTypes.linearGlassProjectedDimension)
      mainLineDist = this.mainLineScalarMax * 4.0

    var scaledStartPnt = new THREE.Vector3(this.startPoint.x, this.startPoint.y, this.startPoint.z)
    scaledStartPnt.addScaledVector(this.extendDirection, mainLineDist)
    var scaledEndPnt = new THREE.Vector3(this.endPoint.x, this.endPoint.y, this.endPoint.z)
    scaledEndPnt.addScaledVector(this.extendDirection, mainLineDist)

    var scaledMidPnt = new THREE.Vector3(this.midPoint.x, this.midPoint.y, this.midPoint.z)
    scaledMidPnt.addScaledVector(this.extendDirection, mainLineDist)

    // If the dimension is really small, push the label outside of the extent lines
    if(this._totalLength < 30 * dimScale) {
      scaledMidPnt.addScaledVector(this.extendDirection, dimScale * 10)
    }
 
    
    // Lines are split into 2 to be on either side of the text label
    if(this.dimensionLineAObj && this.dimensionLineBObj) {
      let refDistance = 1000    // Bit of black magic / trial & error to generate these values
      let paddingScalar = 0.025
      
      if(dimensionDistance > 0) {
        let lengthScalar = 1.0 / (dimensionDistance / refDistance)
        var labelScale = Math.min(dimScale * this.textScalar, this.textScalarMax)
        let lineScale = Math.max(Math.min(0.5 - labelScale * paddingScalar * lengthScalar, 1.0), 0)

        this.dimensionLineAObj.position.copy(scaledStartPnt)
        this.dimensionLineAObj.scale.set(lineScale, lineScale, 1)
        this.dimensionLineBObj.position.copy(scaledEndPnt)
        this.dimensionLineBObj.scale.set(lineScale, lineScale, 1)
      }
    }

    // Arrows
    // Hide arrows if the line distance is too short & zoomed out too much
    var arrowVisible = true
    if(dimensionDistance > 0) {
      let arrowVisibleFactor = 35.0
      let arrowScalarCheck = arrowVisibleFactor * dimScale
      if(this._totalLength < arrowScalarCheck)
        arrowVisible = false
    }

    if(this.startArrowObj) {
      this.startArrowObj.position.copy(scaledStartPnt)
      this.startArrowObj.visible = arrowVisible
    }
    if(this.endArrowObj) {
      this.endArrowObj.position.copy(scaledEndPnt)
      this.endArrowObj.visible = arrowVisible
    }
    
    if(this.dimensionLabelObj)
      this.dimensionLabelObj.position.copy(scaledMidPnt)

    // End cap scaling
    if(this.startCapLineObj) {
      scaledStartPnt.copy(this.startPoint).addScaledVector(this.extendDirection, mainLineDist * 1.1)
      this.updateDimensionLine(this.startCapLineObj, this.startPoint, scaledStartPnt, extLineColour)
    }
    if(this.endCapLineObj) {
      scaledEndPnt.copy(this.endPoint).addScaledVector(this.extendDirection, mainLineDist * 1.1)
      this.updateDimensionLine(this.endCapLineObj, this.endPoint, scaledEndPnt, extLineColour)
    }
  }

  // onMouseDownEvent
  onMouseDownEvent(ev, mouseData) {
    
  }

  // onMouseMoveEvent
  onMouseMoveEvent(ev, mouseData) {
    if(this.isReadOnly)
      return
    if(!this.editor.isSelectEnabled())
      return

    // If over dimension label, change to a hand
    this.labelList.forEach(obj => {

      if(mouseData.raycaster.intersectObject(obj, true).length > 0) {
        this.editor.setMouseCursorHand()
      }
    })
  }

  // onMouseUpEvent
  onMouseUpEvent(ev, mouseData, intersectionInfo) {
    if(this.isReadOnly)
      return

    this.labelList.forEach((obj, index) => {
      if(obj.id == intersectionInfo.objectId) {

        this.editor.dimensionLabelClicked(this)
      }
    })      
  }

  // Get total length of the linear dimension
  getTotalLength() {
    return this._totalLength
  }

  // Build the linear dimension from an array of Vector3 points
  // Automatically calculates directions
  buildFromPoints(pointList) {
    if(pointList.length < 2)
      return

    this.clearPoints()
    let dir = pointList[pointList.length-1].clone().sub(pointList[0])
    let dist = dir.length()
    dir.normalize()

    this.lineDirection = new THREE.Vector3(dir.x, dir.y, 0)
    this.extendDirection = new THREE.Vector3(dir.y, -dir.x, 0)

    // Always point away from the shape
    if(this.glassShape) {
      let midpoint = pointList[pointList.length-1].clone().add(pointList[0]).multiplyScalar(0.5)
      midpoint.addScaledVector(this.extendDirection, dist * 0.25)
      
      if(this.glassShape.isPointInsideShape(midpoint))
        this.extendDirection.negate()
    }

    pointList.forEach(pnt => {
      this.addPoint(pnt)
    })
    this.build()
  }

  // Add a measurement point to the list
  addPoint(point) {
    this.points.push(point)
  }

  // Build & create all the objects
  build() {
    if(this.points.length == 0)
      return

    let userConfig = this.editor.getUserConfig()
    let displayPrecision = userConfig.linearDimensionPrecision

    let extLineColour = this.editor.getTheme().dimExtLineColour
    let lineColour = this.editor.getTheme().dimLineColour
    let labelColour = this.editor.getTheme().dimLabelColour
    let highlightLabelColour = this.editor.getTheme().dimLabelHighlightColour
    let fixedLabelColour = this.editor.getTheme().dimFixedLabelColour
    let lineOpacity = this.editor.getTheme().dimLineOpacity

    this._lastRenderDimScale = 0
    this._lastRenderDimLength = 0

    var opacityScalar = 1.0

    // Dim for shape editing mode
    if(this.editor.isShapeEditingMode())
      opacityScalar = 0.33

    this._totalLength = 0

    // Adjust properties
    this.listenMouseEvents = !this.isReadOnly

    let dispPoint = this.displayPoint || this.points[0]

    let extendPlane = new THREE.Plane()
    extendPlane.setFromNormalAndCoplanarPoint(this.extendDirection, dispPoint.clone())

    var angleRads = Math.atan2(this.lineDirection.y, this.lineDirection.x)
    
    // Snap angle to nearest degree to prevent flipped text on precision errors
    angleRads = MathsUtils.roundNumber(angleRads / (Math.PI / 180.0)) * (Math.PI / 180.0)


    // Note: Assumes now just 2 points
    var prevPos = null
    this.points.forEach((pnt, index) => {
      var outPnt = new THREE.Vector3()
      var distance = 0

      extendPlane.projectPoint(pnt, outPnt)
      
      if(index > 0)
        distance = MathsUtils.roundNumber(outPnt.distanceTo(prevPos), displayPrecision)
      this._totalLength += distance
      
      // Arrow
      var arrow = null
      if(index == 0) {
        if(!this.startArrowObj)
          this.startArrowObj = this.createArrow()
        arrow = this.startArrowObj
      } else {
        if(!this.endArrowObj)
          this.endArrowObj = this.createArrow()
        arrow = this.endArrowObj
      }

      arrow.position = outPnt
      arrow.rotation.z = angleRads
      if(index == this.points.length-1)
        arrow.rotation.z = angleRads + Math.PI

      var colour = this.editor.getTheme().dimArrowColour
      var opacity = this.editor.getTheme().dimArrowOpacity
      if(this.isErrorFlag) {
        colour = this.editor.getTheme().dimErrorColour
        opacity = this.editor.getTheme().dimErrorOpacity
      }
      arrow.material.color.set(colour)
      arrow.material.opacity = opacity * opacityScalar


      // Main line
      if(index > 0) {
        let startPnt = prevPos.clone()
        let endPnt = outPnt.clone()
        let midPoint = endPnt.clone().sub(startPnt).multiplyScalar(0.5).add(startPnt)

        this.startPoint.copy(startPnt)
        this.endPoint.copy(endPnt)
        this.midPoint.copy(midPoint)

        if(!this.dimensionLineAObj)
          this.dimensionLineAObj = this.createDimensionLine(startPnt, endPnt, lineColour, lineOpacity * opacityScalar)
        else
          this.updateDimensionLine(this.dimensionLineAObj, startPnt, endPnt, lineColour, lineOpacity * opacityScalar)

        if(!this.dimensionLineBObj)
          this.dimensionLineBObj = this.createDimensionLine(endPnt, startPnt, lineColour, lineOpacity * opacityScalar)
        else
          this.updateDimensionLine(this.dimensionLineBObj, endPnt, startPnt, lineColour, lineOpacity * opacityScalar)
      }

      // Cap lines
      // Default distance. Will be adjusted during rendering
      let startCap = outPnt.clone()
      let endCap = startCap.clone().addScaledVector(this.extendDirection, 10)
      
      // Reuse capline & update if needed
      if(index == 0) {
        if(!this.startCapLineObj)
          this.startCapLineObj = this.createDimensionLine(startCap, endCap, extLineColour, lineOpacity * opacityScalar)
        else
          this.updateDimensionLine(this.startCapLineObj, startCap, endCap, extLineColour, lineOpacity * opacityScalar)
      }
      if(index == 1) {
        if(!this.endCapLineObj)
          this.endCapLineObj = this.createDimensionLine(startCap, endCap, extLineColour, lineOpacity * opacityScalar)
        else
          this.updateDimensionLine(this.endCapLineObj, startCap, endCap, extLineColour, lineOpacity * opacityScalar)
      }


      // Label
      if(index > 0) {
        let maxReadableAngle = 110

        let labelText = this.calculateLinearLabelText(distance)

        if(!this.dimensionLabelObj)
          this.dimensionLabelObj = this.createDimensionLabel(this.midPoint, labelText)

        this.dimensionLabelObj.anchorY = 'middle'
        this.dimensionLabelObj.position.set(this.midPoint.x, this.midPoint.y, this.midPoint.z)
        this.dimensionLabelObj.text = labelText
        this.dimensionLabelObj.color = this._isUserFixed ? fixedLabelColour : labelColour
        this.dimensionLabelObj.fontSize = this.isHighlighted ? this.highlightFontSize : this.fontSize

        if(this.isHighlighted)
          this.dimensionLabelObj.color = highlightLabelColour

        if(this.isReadOnly) {
          this.dimensionLabelObj.color = labelColour
          this.dimensionLabelObj.material.opacity = this.editor.getTheme().dimReadOnlyOpacity
        } else {
          this.dimensionLabelObj.material.opacity = 1.0
        }

        if(this.isErrorFlag) {
          this.dimensionLabelObj.color = this.editor.getTheme().dimErrorColour
          this.dimensionLabelObj.material.opacity = this.editor.getTheme().dimErrorOpacity
        }

        // Special colour for overall editable dimension
        if(this.dimType == GlassDesignerTypes.DimensionTypes.linearGlassOverallDimension && !this.isReadOnly) {
          this.dimensionLabelObj.color = this.editor.getTheme().dimOverallActiveColour
          this.dimensionLabelObj.material.opacity = this.editor.getTheme().dimOverallActiveOpacity
        }

        this.dimensionLabelObj.material.opacity *= opacityScalar

        var textAngleDeg = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(angleRads))
        if(textAngleDeg >= 180)
          textAngleDeg -= 180

        // Flip text so it's not upside down
        let readableDist = MathsUtils.calculateShortestAngleDistanceDegrees(0, textAngleDeg)
        if(readableDist > maxReadableAngle)
          textAngleDeg = MathsUtils.wrapAngleDegrees(textAngleDeg - 180)

        // If extends direction is heading bottom/left on screen position, anchor label to top
        let screenAng = this.extendDirection.dot(new THREE.Vector3(-1,1,0))
        //if(screenAng > 0.25)
          //this.dimensionLabelObj.anchorY = 'top'

        // If text going opposite the dim line, anchor label to top
        //if(MathsUtils.calculateShortestAngleDistanceDegrees(textAngleDeg, MathsUtils.radiansToDegrees(angleRads)) > 90)
          //this.dimensionLabelObj.anchorY = 'top'

        this.dimensionLabelObj.rotation.z = MathsUtils.degreesToRadians(textAngleDeg)
      }

      prevPos = outPnt
    })
  }



  

}
